import { ExportOption, Opacity, Positioning, RatioOption, Size, VisualEditingSteps } from 'constants/misc/editingOptions'
import { JobType, useJobPoll, useLoadWatermarkLogo, useSubmitEditedVisualsNoSubscription, useTriggerVisualsEditing, useUploadWatermarkLogo, useVisualsEditingTemplateOptions } from 'dataQueries'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { LoadEditedVisualStore, RatioDTO } from 'models/visualsEditing'
import constate from 'constate'
import { exportTypeToEditedVisualsType } from 'constants/visualEditing/visualEditingFiles'
import { logAnalyticsEvent } from 'utils/analytics'
import { mutationToAPIRequest, queryToAPIRequest } from 'utils/reactQuery'
import { patternList } from 'constants/misc'
import { useGalleryAssignment } from 'components/pages/Gallery/_main/contexts/GalleryAssignment.context'
import { useGalleryVisualsMeta } from '../Gallery/_main/contexts'
import { useUserData } from 'components/contexts/UserDataContext'
import { useVisualsEditingDownload } from './VisualsEditingDownload.context'
import { useWorkspacesProfileContext } from '../Client/WorkspacesProfile/_main/WorkspacesProfile.context'

const DEFAULT_RATIO_PATTERN = patternList[0]

export const [VisualsEditingContextProvider, useVisualsEditing] = constate(({
  assignmentId,
  selected
}: {
  assignmentId: string,
  selected: Set<string>
}) => {
  const {
    baseUserData,
    organizationData,
    currentUserWorkspace
  } = useUserData()
  const { product, realtyId } = useGalleryAssignment()
  const { allUserWorkspacesData } = useWorkspacesProfileContext()
  const {
    archiveEditingVisualsMutation,
    downloadUrl,
    hasDownloadFailed,
    isDownloadReady,
    isDownloadRunning,
  } = useVisualsEditingDownload()
  const {
    purchasedVisualsKeys,
  } = useGalleryVisualsMeta()

  const uploadWatermarkMutation = useUploadWatermarkLogo()
  const [uploadProgress, setUploadProgress] = useState(0)

  // Loaded edited visuals state (from Web Workers)
  const [loadedEditedVisuals, setLoadedEditedVisuals] = useState<LoadEditedVisualStore>({})

  // Data to all workspaces the user is a member of
  const currentWorkspaceData = allUserWorkspacesData?.find((workspace) => workspace.workspaceId === currentUserWorkspace?.id)

  const visualEditingFilenames = Array.from(selected).filter(filename => purchasedVisualsKeys.has(filename))

  // Job ID for editing visuals
  const [editingJobId, setEditingJobId] = useState('')

  // Visuals Editing flow progress
  const [activeFlowStep, setActiveFlowStep] = useState(VisualEditingSteps.SETTINGS)

  // Save template (Preview page action) status state
  const [saveTemplateStatusSuccess, setSaveTemplateStatusSuccess] = useState(false)

  // Settings of editor options
  const [isTemplateApplied, setIsTemplateApplied] = useState(false)
  const [isRatioActive, setIsRatioActive] = useState(false)
  const [isWatermarkActive, setIsWatermarkActive] = useState(false)
  const [selectedRatioPattern, setSelectedRatioPattern] = useState<RatioDTO>(DEFAULT_RATIO_PATTERN)
  const [selectedWatermarkPosition, setSelectedWatermarkPosition] = useState<Positioning>(Positioning.CENTER)
  const [selectedWatermarkSize, setSelectedWatermarkSize] = useState(Size.MEDIUM)
  const [selectedWatermarkOpacity, setSelectedWatermarkOpacity] = useState<number>(Opacity.DEFAULT)
  const [exportOption, setExportOption] = useState<ExportOption>(ExportOption.ORIGINAL)

  const watermarkId = !!currentWorkspaceData?.watermarks.length ? currentWorkspaceData?.watermarks[0].id : null
  const templateId = !!currentWorkspaceData?.visualEditingTemplates.length ? currentWorkspaceData?.visualEditingTemplates[0].id : null

  const savedTemplateOptionsResponse = useVisualsEditingTemplateOptions(currentUserWorkspace?.id, templateId, !!templateId)
  const savedTemplateOptionsData = savedTemplateOptionsResponse?.data

  const isWatermarkLockedToTemplate = savedTemplateOptionsData?.watermarkId === watermarkId

  const loadWatermarkLogoQuery = useLoadWatermarkLogo(currentUserWorkspace?.id, watermarkId)
  const uploadedWatermarkLogo = loadWatermarkLogoQuery.data ?? null

  const showExitPopup = useMemo(() => isWatermarkActive || isRatioActive || exportOption !== ExportOption.ORIGINAL, [exportOption, isRatioActive, isWatermarkActive])

  const triggerVisualEditing = useTriggerVisualsEditing()
  const jobPoll = useJobPoll(JobType.VISUALS_EDITING)

  const sendEditedVisualsNoSubscription = useSubmitEditedVisualsNoSubscription()

  // The user hasn't made any changes to the visuals. We use this to disable submit button and show validation message.
  const processPreviewDisabled = useMemo(() => (
    (!isWatermarkActive || (isWatermarkActive && !uploadedWatermarkLogo?.signedURL)) &&
    (!isRatioActive || (isRatioActive && selectedRatioPattern.value === RatioOption.ORIGINAL)) &&
    (exportOption === ExportOption.ORIGINAL)
  ), [exportOption, isRatioActive, isWatermarkActive, selectedRatioPattern.value, uploadedWatermarkLogo?.signedURL])

  const handleEditingOptionChange = (callback: () => void) => {
    // Update the isTemplateApplied state to indicate that the template is no longer applied after user changes options
    if (isTemplateApplied) setIsTemplateApplied(false)
    // Enable user to re-save template when they change options by going back on Settings page after template was saved
    if (saveTemplateStatusSuccess) setSaveTemplateStatusSuccess(false)

    callback()
  }

  const logEditEvent = useCallback((eventName: string, params?: {}) => {
    logAnalyticsEvent(eventName, {
      userEmail: baseUserData?.email,
      organizationId: organizationData?.id,
      planId: currentUserWorkspace?.id,
      assignmentId,
      ...(realtyId && { realtyId }),
      ...params
    })
  }, [assignmentId, baseUserData?.email, organizationData?.id, currentUserWorkspace?.id, realtyId])

  const retryDownload = useCallback(() => {
    logEditEvent('Retry Visuals Editing Download')
    const visualsType = exportTypeToEditedVisualsType[exportOption]

    if (editingJobId) {
      if (hasDownloadFailed) {
        archiveEditingVisualsMutation.mutate({
          assignmentId,
          exportId: editingJobId,
          types: [visualsType],
          filenames: visualEditingFilenames,
        })
      }
      if (downloadUrl) {
        window.location.replace(downloadUrl)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logEditEvent, editingJobId, assignmentId, downloadUrl, hasDownloadFailed, visualEditingFilenames, exportOption])

  // Logs for editor options
  const productParams = {
    productCategory: product?.category,
    productKind: product?.kind,
    numberOfVisuals: visualEditingFilenames.length
  }

  const logSubmitEdit = () => {
    const options = {
      isLogoEdited: isWatermarkActive,
      isRatioEdited: isRatioActive,
      isExportEdited: exportOption !== ExportOption.ORIGINAL
    }

    if (isWatermarkActive) {
      Object.assign(options, {
        logoSize: selectedWatermarkSize,
        logoOpacity: selectedWatermarkOpacity,
        logoPositioning: selectedWatermarkPosition
      })
    }

    if (isRatioActive) {
      Object.assign(options, { ratio: selectedRatioPattern })
    }

    Object.assign(options, { exportOption })

    logEditEvent('Submit Bulk Editing', { ...options, ...productParams })
  }

  const resetWatermarkContext = () => {
    setSelectedWatermarkSize(Size.MEDIUM)
    setSelectedWatermarkOpacity(Opacity.DEFAULT)
    setSelectedWatermarkPosition(Positioning.CENTER)
    setIsWatermarkActive(false)
    uploadWatermarkMutation.reset()
    setUploadProgress(0)
  }

  const resetVisualsEditingContext = () => {
    resetWatermarkContext()
    setIsRatioActive(false)
    setSelectedRatioPattern(DEFAULT_RATIO_PATTERN)
    setExportOption(ExportOption.ORIGINAL)
    setIsTemplateApplied(false)
  }

  useEffect(() => {
    if (triggerVisualEditing.isSuccess && triggerVisualEditing.data) {
      const { id } = triggerVisualEditing.data.data
      setEditingJobId(id)
    }
  }, [triggerVisualEditing.data, triggerVisualEditing.isSuccess])

  useEffect(() => {
    if (sendEditedVisualsNoSubscription.isSuccess && sendEditedVisualsNoSubscription.data) {
      const { id } = sendEditedVisualsNoSubscription.data.data
      setEditingJobId(id)
    }
  }, [sendEditedVisualsNoSubscription.data, sendEditedVisualsNoSubscription.isSuccess])

  useEffect(() => {
    // Set data when user wants to apply template in VE settings page
    if (isTemplateApplied && savedTemplateOptionsData) {
      setIsWatermarkActive(Boolean(savedTemplateOptionsData.watermarkId))
      setSelectedWatermarkSize(savedTemplateOptionsData.watermarkSize ?? Size.MEDIUM)
      setSelectedWatermarkOpacity(savedTemplateOptionsData.watermarkOpacity ?? Opacity.DEFAULT)
      setSelectedWatermarkPosition(savedTemplateOptionsData.watermarkPositioning ?? Positioning.CENTER)
      setIsRatioActive(Boolean(savedTemplateOptionsData.imageRatio))
      setSelectedRatioPattern(patternList.find((patttern) => patttern.value === savedTemplateOptionsData.imageRatio) ?? DEFAULT_RATIO_PATTERN)
      setExportOption(ExportOption[savedTemplateOptionsData.export])
    }
  }, [isTemplateApplied, savedTemplateOptionsData])

  return {
    assignmentId,
    isWatermarkActive,
    setIsWatermarkActive,
    selectedWatermarkPosition,
    setSelectedWatermarkPosition,
    selectedWatermarkSize,
    setSelectedWatermarkSize,
    selectedWatermarkOpacity,
    setSelectedWatermarkOpacity,
    isRatioActive,
    setIsRatioActive,
    selectedRatioPattern,
    setSelectedRatioPattern,
    exportOption,
    setExportOption,
    resetWatermarkContext,
    productParams,
    logEditEvent,
    logSubmitEdit,
    showExitPopup,
    processPreviewDisabled,
    uploadedWatermarkLogo,
    uploadWatermarkMutation,
    uploadProgress,
    setUploadProgress,
    loadWatermarkLogoQuery,
    setActiveFlowStep,
    activeFlowStep,
    visualEditingFilenames,
    downloadUrl,
    hasDownloadFailed,
    isDownloadReady,
    isDownloadRunning,
    retryDownload,
    editingJobId,
    currentWorkspaceData,
    watermarkId,
    templateId,
    setIsTemplateApplied,
    isTemplateApplied,
    resetVisualsEditingContext,
    savedTemplateOptionsResponse,
    savedTemplateOptionsData,
    handleEditingOptionChange,
    setSaveTemplateStatusSuccess,
    saveTemplateStatusSuccess,
    isWatermarkLockedToTemplate,
    triggerVisualEditing,
    jobPoll,
    sendEditedVisualsNoSubscription,
    archiveEditingVisualsMutation,
    loadedEditedVisuals,
    setLoadedEditedVisuals,
  }
})
